import http from "../http-common";
import { DecryptData } from "../util/DataEncryption";
import {
  getDefaultBooleanOptions,
  PollingScreenStringLabels,
} from "../util/Strings";
import pollingEndpoints from "./pollingEndpoints";

function applyAuthToken() {
  const tokenStr = sessionStorage.getItem("token")
    ? DecryptData(sessionStorage.getItem("token"))
    : null;
  http.defaults.headers.Authorization = `Bearer ${tokenStr}`;
}

function throwApiError(error, fallbackMessage) {
  const payload = error?.response?.data;
  const message =
    payload?.message ||
    payload?.description ||
    (typeof payload === "string" ? payload : null) ||
    error?.message ||
    fallbackMessage;
  const err = new Error(message);
  err.status = error?.response?.status;
  throw err;
}

function extractResponseData(payload) {
  if (!payload) {
    return null;
  }
  if (payload.data !== undefined && payload.data !== null && typeof payload === "object") {
    if (payload._id || payload.polls || payload.visitId) {
      return payload;
    }
    return payload.data;
  }
  return payload;
}

function stringifyId(value) {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value.toString === "function") {
    return value.toString();
  }
  return String(value);
}

function isMongoObjectId(value) {
  return /^[a-f0-9]{24}$/i.test(String(value || ""));
}

function normalizeOptionLabels(options) {
  return (options || [])
    .slice()
    .sort((a, b) => {
      const orderA = typeof a === "object" ? a.order || 0 : 0;
      const orderB = typeof b === "object" ? b.order || 0 : 0;
      return orderA - orderB;
    })
    .map((option) => {
      if (typeof option === "string") {
        return option;
      }
      return option?.label || "";
    });
}

function normalizeQuestion(question, index) {
  if (!question) {
    return null;
  }
  return {
    id: stringifyId(question.id || question._id) || `q_${index + 1}`,
    type: question.type,
    prompt: question.prompt || "",
    required: Boolean(question.required),
    options:
      question.type === "boolean"
        ? getDefaultBooleanOptions()
        : normalizeOptionLabels(question.options),
  };
}

export function normalizePoll(raw) {
  if (!raw) {
    return null;
  }
  const id = stringifyId(raw._id || raw.id || raw.pollId);
  const questions = (raw.questions || [])
    .map(normalizeQuestion)
    .filter(Boolean);

  return {
    _id: id,
    visitId: stringifyId(raw.visitId),
    name: raw.name || "",
    status: raw.status || "draft",
    showPollResults: Boolean(raw.showPollResults),
    questions,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    resultsSummary: normalizeResultsSummary(raw.resultsSummary || raw.results || raw.pollResults),
  };
}

export function normalizeResultsSummary(raw) {
  if (!raw) {
    return null;
  }
  if (Array.isArray(raw.byQuestion) && raw.byQuestion.length) {
    return {
      totalRespondents: raw.totalRespondents ?? raw.totalResponses ?? 0,
      byQuestion: raw.byQuestion.map((row) => ({
        questionId: stringifyId(row.questionId || row._id),
        prompt: row.prompt || "",
        options: (row.options || []).map((opt) => ({
          label: opt.label || "",
          count: opt.count ?? 0,
          pct: opt.pct ?? 0,
        })),
      })),
    };
  }
  return null;
}

function normalizePollList(payload) {
  const data = extractResponseData(payload);
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data.map(normalizePoll).filter(Boolean);
  }
  if (Array.isArray(data.polls)) {
    return data.polls.map(normalizePoll).filter(Boolean);
  }
  if (data._id || data.id || data.pollId) {
    return [normalizePoll(data)].filter(Boolean);
  }
  return [];
}

function sortPollList(list) {
  return [...list].sort((a, b) => {
    const ta = new Date(a.updatedAt || 0).getTime();
    const tb = new Date(b.updatedAt || 0).getTime();
    return tb - ta;
  });
}

function toApiQuestionOptions(question) {
  if (question.type === "boolean") {
    return getDefaultBooleanOptions().map((label, index) => ({
      label,
      order: index + 1,
    }));
  }
  return (question.options || [])
    .map((option) => (typeof option === "string" ? option : option?.label || ""))
    .filter((label) => label.trim())
    .map((label, index) => ({
      label: label.trim(),
      order: index + 1,
    }));
}

function toApiQuestions(questions) {
  return (questions || []).map((question, index) => {
    const item = {
      order: index + 1,
      type: question.type,
      prompt: (question.prompt || "").trim(),
      required: Boolean(question.required),
      options: toApiQuestionOptions(question),
    };
    const questionId = stringifyId(question.id || question._id);
    if (isMongoObjectId(questionId)) {
      item._id = questionId;
    }
    return item;
  });
}

function toApiCreateBody(visitId, body) {
  return {
    visitId,
    name: (body.name || "").trim(),
    status: body.status || "draft",
    showPollResults: Boolean(body.showPollResults),
    questions: toApiQuestions(body.questions),
  };
}

function toApiUpdateBody(body) {
  return {
    name: (body.name || "").trim(),
    status: body.status || "draft",
    showPollResults: Boolean(body.showPollResults),
    questions: toApiQuestions(body.questions),
  };
}

async function fetchVisitPollList(visitId) {
  applyAuthToken();
  const response = await http.get(pollingEndpoints.visitPolls(visitId));
  return sortPollList(normalizePollList(response.data));
}

const PollingAPI = {
  endpoints: pollingEndpoints,

  /** GET /visits/:visitId/polls */
  async getByVisitId(visitId) {
    try {
      const list = await fetchVisitPollList(visitId);
      return { data: list, status: 200 };
    } catch (error) {
      throwApiError(error, PollingScreenStringLabels.errorFetchFailed);
    }
    return { data: [], status: 200 };
  },

  /** GET /visits/:visitId/polls/:pollId */
  async getById(visitId, pollId) {
    try {
      applyAuthToken();
      const response = await http.get(pollingEndpoints.poll(visitId, pollId));
      const poll = normalizePoll(extractResponseData(response.data));
      if (!poll?._id) {
        const err = new Error(PollingScreenStringLabels.errorPollNotFound);
        err.status = 404;
        throw err;
      }
      return { data: poll, status: 200 };
    } catch (error) {
      if (error.status) {
        throw error;
      }
      throwApiError(error, PollingScreenStringLabels.errorPollNotFound);
    }
    return { data: null, status: 404 };
  },

  /**
   * Results are not on a dedicated REST route today. For live polls the console
   * relies on Socket.IO `poll:results`. This helper reads embedded summary when
   * the backend attaches it on GET poll (often empty).
   */
  async getResults(visitId, pollId) {
    const { data: poll } = await this.getById(visitId, pollId);
    if (poll.status === "draft") {
      const err = new Error(PollingScreenStringLabels.errorResultsDraft);
      err.status = 403;
      throw err;
    }
    return {
      data: {
        pollId: poll._id,
        status: poll.status,
        resultsSummary: poll.resultsSummary || null,
      },
      status: 200,
    };
  },

  /** POST /visits/:visitId/polls */
  async create(visitId, body) {
    try {
      applyAuthToken();
      const response = await http.post(
        pollingEndpoints.visitPolls(visitId),
        toApiCreateBody(visitId, body)
      );
      const data = normalizePoll(extractResponseData(response.data));
      const visitPolls = await fetchVisitPollList(visitId);
      return { data, visitPolls, status: response.status || 201 };
    } catch (error) {
      throwApiError(error, PollingScreenStringLabels.errorSaveFailed);
    }
    return { data: null, visitPolls: [], status: 500 };
  },

  /** PUT /visits/:visitId/polls/:pollId */
  async update(pollId, visitId, body) {
    try {
      applyAuthToken();
      const response = await http.put(
        pollingEndpoints.poll(visitId, pollId),
        toApiUpdateBody(body)
      );
      const data = normalizePoll(extractResponseData(response.data));
      const visitPolls = await fetchVisitPollList(visitId);
      return { data, visitPolls, status: response.status || 200 };
    } catch (error) {
      throwApiError(error, PollingScreenStringLabels.errorSaveFailed);
    }
    return { data: null, visitPolls: [], status: 500 };
  },

  /** PUT /visits/:visitId/polls/:pollId/close */
  async close(visitId, pollId) {
    try {
      applyAuthToken();
      const response = await http.put(pollingEndpoints.closePoll(visitId, pollId));
      const data = normalizePoll(extractResponseData(response.data));
      const visitPolls = await fetchVisitPollList(visitId);
      return { data, visitPolls, status: response.status || 200 };
    } catch (error) {
      throwApiError(error, PollingScreenStringLabels.errorSaveFailed);
    }
    return { data: null, visitPolls: [], status: 500 };
  },

  /** DELETE /deletePoll/:pollId */
  async deletePoll(pollId, visitId) {
    try {
      applyAuthToken();
      const response = await http.delete(pollingEndpoints.deletePoll(pollId));
      const visitPolls = await fetchVisitPollList(visitId);
      return {
        pollId,
        visitPolls,
        status: response.status || 202,
      };
    } catch (error) {
      throwApiError(error, PollingScreenStringLabels.errorDeleteFailed);
    }
    return { pollId, visitPolls: [], status: 500 };
  },
};

export default PollingAPI;
